import useSEO from "@/hooks/useSEO";
import { SEO, SITE } from "@/config/site";
import { Fade, Lines, Reveal } from "@/components/motion";
import { BookingButton, WhatsAppAssist } from "@/components/CTA";

const CREDENTIALS = [
  {
    title: "Formazione accademica",
    lines: [
      "Dottore in Scienze delle Attività Motorie e Sportive — Università di Bologna",
      "Dottore Magistrale in Scienze dell'Alimentazione — Università di Firenze",
    ],
  },
  {
    title: "Iscrizione professionale",
    lines: [
      "Iscritto all'Ordine dei Biologi dell'Emilia-Romagna e delle Marche",
      "Sezione A, n. 4982",
    ],
  },
];

export default function ChiSono() {
  useSEO(SEO.chiSono);
  return (
    <>
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-32 sm:pt-44 pb-20 sm:pb-28">
        <Fade>
          <p className="eyebrow text-leaf-deep">Chi sono</p>
        </Fade>
        <div className="mt-7 grid items-start gap-14 lg:grid-cols-[1.15fr_1fr] lg:gap-20">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              <Lines delay={0.2} lines={["Dott. Michele", "Ceccarelli"]} />
            </h1>
            <Fade delay={0.6}>
              <p className="mt-6 text-sm font-bold uppercase tracking-[0.2em] text-wave-deep">
                {SITE.professional.role}
              </p>
              <p className="mt-9 max-w-xl text-base md:text-lg leading-relaxed text-ink/70">
                Sono un biologo nutrizionista, dottore in Scienze Motorie ed ex preparatore
                atletico. Ho scelto di unire queste competenze partendo da una convinzione precisa:
                alimentazione e attività fisica sono due pilastri fondamentali per la salute, la
                prevenzione e la performance.
              </p>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/60">
                Quando sono ben programmati e adattati alla persona, nutrizione e movimento possono
                contribuire in modo concreto al benessere, alla composizione corporea, alla qualità
                della vita e, quando l'obiettivo lo richiede, al miglioramento della prestazione
                fisica. Sono anche un supporto importante nella gestione di molte condizioni, sempre
                nel rispetto delle competenze professionali e del quadro clinico individuale.
              </p>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/60">
                Il mio lavoro parte sempre dall'ascolto. Prima di qualsiasi piano alimentare o
                programma di allenamento c'è una valutazione attenta della persona: obiettivi,
                abitudini, stile di vita, esigenze, difficoltà e punto di partenza. La scienza
                orienta ogni scelta, ma è la realtà quotidiana della persona a dare forma al
                percorso.
              </p>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/60">
                Cerco inoltre di applicare in prima persona gli stessi principi che propongo. Il
                movimento fa parte della mia quotidianità e curo la mia alimentazione con un
                approccio equilibrato, flessibile e sostenibile, senza estremismi. Questo mi
                permette di comprendere meglio non solo la teoria, ma anche le difficoltà pratiche
                che possono emergere nel conciliare salute, forma fisica, performance e vita reale.
              </p>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/60">
                È questo l'approccio che cerco di trasmettere ai miei pazienti: non perfezione, ma
                un equilibrio costruito nel tempo, scientificamente fondato e realmente compatibile
                con la propria vita.
              </p>
            </Fade>
          </div>
          <Fade delay={0.4} className="relative">
            <img
              src={SITE.images.portraitHome}
              alt="Dott. Michele Ceccarelli, Biologo Nutrizionista e Dott. in Scienze Motorie, con il logo Nutrimove Lab"
              className="w-full [filter:drop-shadow(0_28px_36px_rgba(16,22,20,0.14))]"
              width={1100}
              height={1135}
              fetchPriority="high"
              data-testid="about-portrait"
            />
          </Fade>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-mist" aria-label="Profilo professionale">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
          <Reveal>
            <p className="eyebrow text-wave-deep">Profilo professionale</p>
            <h2 className="mt-6 max-w-2xl font-display text-3xl sm:text-4xl tracking-tight text-balance">
              Preparazione scientifica, approccio pratico
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-ink/60">
              La formazione e l'iscrizione professionale del Dott. Ceccarelli.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {CREDENTIALS.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.07}>
                <div
                  data-testid={`credential-slot-${i + 1}`}
                  className="h-full rounded-2xl border border-ink/10 bg-white p-7 sm:p-9"
                >
                  <p className="eyebrow text-leaf-deep">{item.title}</p>
                  <ul className="mt-5 space-y-4">
                    {item.lines.map((line) => (
                      <li key={line} className="font-display text-lg sm:text-xl leading-snug tracking-tight text-ink/85">
                        {line}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-ink/10" aria-label="La visione">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
          <Reveal className="mx-auto max-w-3xl text-center">
            <blockquote className="font-display text-2xl sm:text-3xl lg:text-4xl italic leading-snug tracking-tight text-ink/85 text-balance">
              “Nutrizione e movimento: due aree, un'unica visione della persona.”
            </blockquote>
          </Reveal>
          <div className="mx-auto mt-16 grid max-w-4xl gap-12 lg:grid-cols-[1.3fr_1fr] lg:items-center">
            <Reveal>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-balance">
                Vuoi iniziare il tuo percorso?
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-ink/60">
                Prenota una prima visita per iniziare un percorso personalizzato di nutrizione,
                allenamento o nutrizione e allenamento integrati.
              </p>
              <div className="mt-9">
                <BookingButton label="Prenota una visita" testId="about-booking-cta" />
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <WhatsAppAssist testId="about-whatsapp-assist" />
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
